import axios from 'axios'
import api from '@/lib/api/crate-core-api-client'
import {
  MarketplaceAssetPurchaseContextItem,
  MarketplaceAssetPurchaseOptions,
  MarketplaceAssetTeamState,
  MarketplaceBundle,
  MarketplaceCart,
  MarketplaceCartAddItemInput,
  MarketplaceCheckoutResponse,
  MarketplaceCollectionResponse,
  MarketplaceOrder,
  MarketplacePurchasedAsset,
} from '@/lib/marketplace/types'

const isUnauthorizedError = (error: unknown) => axios.isAxiosError(error) && error.response?.status === 401

export async function getMarketplaceAssetPurchaseContext(
  assetUuids: string[],
  { signal }: { signal?: AbortSignal } = {}
): Promise<MarketplaceAssetPurchaseContextItem[]> {
  if (!assetUuids.length) {
    return []
  }

  try {
    const { data } = await api.post<MarketplaceCollectionResponse<MarketplaceAssetPurchaseContextItem>>(
      '/marketplace/assets/purchase-context',
      {
        asset_uuids: assetUuids,
      },
      { signal }
    )

    return data.data ?? []
  } catch (error) {
    if (isUnauthorizedError(error)) {
      return []
    }

    throw error
  }
}

export async function getMarketplaceAssetTeamState(
  assetUuids: string[],
  { signal }: { signal?: AbortSignal } = {}
): Promise<MarketplaceAssetTeamState> {
  if (!assetUuids.length) {
    return {
      owned_asset_uuids: [],
      in_cart_asset_uuids: [],
    }
  }

  try {
    const { data } = await api.post<MarketplaceAssetTeamState>(
      '/marketplace/assets/team-state',
      {
        asset_uuids: assetUuids,
      },
      { signal }
    )

    return data
  } catch (error) {
    if (isUnauthorizedError(error)) {
      return {
        owned_asset_uuids: [],
        in_cart_asset_uuids: [],
      }
    }

    throw error
  }
}

export async function getMarketplaceAssetPurchaseOptions(
  assetUuid: string,
  { signal }: { signal?: AbortSignal } = {}
): Promise<MarketplaceAssetPurchaseOptions> {
  const { data } = await api.get<MarketplaceAssetPurchaseOptions>(`/marketplace/assets/${assetUuid}/purchase-options`, {
    signal,
  })
  return data
}

export async function getMarketplaceCart({ signal }: { signal?: AbortSignal } = {}): Promise<MarketplaceCart | null> {
  try {
    const { data } = await api.get<MarketplaceCart>('/marketplace/cart', { signal })
    return data
  } catch (error) {
    if (isUnauthorizedError(error)) {
      return null
    }

    throw error
  }
}

export async function addMarketplaceCartItem(
  payload: MarketplaceCartAddItemInput,
  { signal }: { signal?: AbortSignal } = {}
) {
  const { data } = await api.post<MarketplaceCart>('/marketplace/cart/items', payload, { signal })
  return data
}

export async function removeMarketplaceCartItem(itemId: number, { signal }: { signal?: AbortSignal } = {}) {
  await api.delete(`/marketplace/cart/items/${itemId}`, { signal })
}

export async function checkoutMarketplaceCart({ signal }: { signal?: AbortSignal } = {}) {
  const { data } = await api.post<MarketplaceCheckoutResponse>(
    '/marketplace/cart/checkout',
    {
      provider: 'stripe',
    },
    { signal }
  )

  return data
}

export async function getMarketplaceOrders({ signal }: { signal?: AbortSignal } = {}) {
  try {
    const { data } = await api.get<MarketplaceCollectionResponse<MarketplaceOrder>>('/marketplace/orders', { signal })
    return data.data ?? []
  } catch (error) {
    if (isUnauthorizedError(error)) {
      return []
    }

    throw error
  }
}

export async function getMarketplacePurchasedAssets({
  orderId,
  signal,
}: {
  orderId?: number | null
  signal?: AbortSignal
} = {}) {
  try {
    const { data } = await api.get<MarketplaceCollectionResponse<MarketplacePurchasedAsset>>(
      '/marketplace/purchases/assets',
      {
        params: orderId ? { order_id: orderId } : undefined,
        signal,
      }
    )
    return data.data ?? []
  } catch (error) {
    if (isUnauthorizedError(error)) {
      return []
    }

    throw error
  }
}

export async function getMarketplaceBundles({ signal }: { signal?: AbortSignal } = {}) {
  const { data } = await api.get<MarketplaceBundle[] | MarketplaceCollectionResponse<MarketplaceBundle>>('/bundles', {
    signal,
  })
  return Array.isArray(data) ? data : data.data ?? []
}
